import { useEffect, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@forecastify/shared';
import type { PagerState } from '@forecastify/ui';
import { useSession } from '../session';
import { parseList, totalOf } from './permissions-hook';
import { pagerOf, usePages } from './use-pages';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 31 Oct 2026 · One page of a list, asked of the server with its filters.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The contracts register, the invoice list and a project's costs each read their whole endpoint
 * and filtered, sorted and paged in memory (`use-row-pages.ts`). They hand this their filters as
 * query parameters instead and get one page back, with the server's filtered total for the pager.
 *
 * ⚠️⚠️ **THE FILTERS ARE THE KEY, AND A CHANGED KEY GOES BACK TO PAGE ONE** — `use-pages.ts`'s trap:
 * offset 400 in a set a filter has cut to twelve rows is an empty page with no error. So `params`
 * is serialised once and both the reset and the read depend on that string, never on the object.
 *
 * ⚠️ **`path` null reads nothing** — a list whose subject has not arrived yet.
 *
 * ⚠️ **`nonce` re-reads the same page** — the caller's live counter and its writes, so a colleague's
 * change or one's own save lands on the page being looked at rather than sending it back to one.
 */
export interface ServerList<T> {
  readonly rows: readonly T[];
  readonly page: PagerState;
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading: boolean;
}

export function useServerList<T>(input: {
  readonly path: string | null;
  /** The filters and the sort — empty values are left off the wire. */
  readonly params: Readonly<Record<string, string>>;
  readonly schema: { safeParse: (value: unknown) => { success: boolean; data?: T } };
  readonly limit?: number;
  readonly nonce?: number;
}): ServerList<T> {
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const schemaRef = useRef(input.schema);
  schemaRef.current = input.schema;

  const limit = input.limit ?? DEFAULT_PAGE_SIZE;
  const pages = usePages(limit);
  const [rows, setRows] = useState<readonly T[]>([]);
  const [loading, setLoading] = useState(true);

  const key = JSON.stringify(
    Object.entries(input.params)
      .filter(([, value]) => value !== '')
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
  const { path } = input;
  const nonce = input.nonce ?? 0;
  const { reset, settle, offset } = pages;

  useEffect(() => {
    reset();
  }, [path, key, reset]);

  useEffect(() => {
    if (path === null || sessionRef.current.workspaceId === null) return;
    const query = new URLSearchParams(JSON.parse(key) as [string, string][]);
    query.set('limit', String(limit));
    if (offset > 0) query.set('offset', String(offset));
    const separator = path.includes('?') ? '&' : '?';

    const run = { live: true };
    void (async () => {
      const response = await sessionRef.current
        .request(`${path}${separator}${query.toString()}`)
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      if (!run.live) return;
      /* ⚠️ A refusal or a failure keeps the rows on screen — the next live change or retry
         re-reads, and an emptied table would read as *this filter matched nothing*. */
      const parsed = body === null ? null : parseList(schemaRef.current, body);
      if (parsed !== null) {
        setRows(parsed);
        settle(totalOf(body));
      }
      setLoading(false);
    })();
    return () => {
      run.live = false;
    };
  }, [path, key, offset, limit, nonce, settle]);

  return { rows, page: pagerOf(pages), loading };
}

/**
 * The summary beside a server list — its footer or its tabs' counts — over the same filters.
 * `null` until it arrives, and when it could not be read.
 */
export function useServerSummary<T>(input: {
  readonly path: string | null;
  readonly params: Readonly<Record<string, string>>;
  readonly schema: { safeParse: (value: unknown) => { success: boolean; data?: T } };
  readonly nonce?: number;
}): T | null {
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const schemaRef = useRef(input.schema);
  schemaRef.current = input.schema;
  const [summary, setSummary] = useState<T | null>(null);

  const key = JSON.stringify(
    Object.entries(input.params)
      .filter(([, value]) => value !== '')
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)),
  );
  const { path } = input;
  const nonce = input.nonce ?? 0;

  useEffect(() => {
    if (path === null || sessionRef.current.workspaceId === null) return;
    const query = new URLSearchParams(JSON.parse(key) as [string, string][]).toString();
    const separator = path.includes('?') ? '&' : '?';
    const run = { live: true };
    void (async () => {
      const response = await sessionRef.current
        .request(query === '' ? path : `${path}${separator}${query}`)
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      if (!run.live) return;
      const parsed = body === null ? null : schemaRef.current.safeParse(body);
      setSummary(parsed?.success === true ? (parsed.data ?? null) : null);
    })();
    return () => {
      run.live = false;
    };
  }, [path, key, nonce]);

  return summary;
}
